import React from 'react';
import { RefreshCw, Search } from 'lucide-react';
import Button from '../ui/Button';
import Dropdown from '../ui/Dropdown';
import Input from '../ui/Input';
import ToggleSwitch from '../ui/ToggleSwitch';
import { isWhiteLabel } from '../../config/tenantConfig';

interface AccountOption {
  value: string;        // composite "<broker>::<client_id>"
  label: string;
  disabled?: boolean;
}

interface Props {
  accountOptions: AccountOption[];
  selectedAccountKey: string;
  onAccountChange: (key: string) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  mismatchesOnly: boolean;
  onMismatchesOnlyChange: (v: boolean) => void;
  fetchedAt: string | null;       // ISO from server
  onRefresh: () => void;
  refreshing: boolean;
  disabled?: boolean;
}

function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const now = Date.now();
  const seconds = Math.max(0, Math.floor((now - ts) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(ts).toLocaleString();
}

const ReconciliationFilterBar: React.FC<Props> = ({
  accountOptions,
  selectedAccountKey,
  onAccountChange,
  searchQuery,
  onSearchChange,
  mismatchesOnly,
  onMismatchesOnlyChange,
  fetchedAt,
  onRefresh,
  refreshing,
  disabled,
}) => {
  // Re-render every 30s so the relative timestamp ticks (purely cosmetic).
  const [, force] = React.useState(0);
  React.useEffect(() => {
    if (!fetchedAt) return;
    const t = setInterval(() => force(x => x + 1), 30000);
    return () => clearInterval(t);
  }, [fetchedAt]);

  const focusRing = isWhiteLabel() ? 'focus:ring-zebu-500' : 'focus:ring-blue-500';
  const lastFetchedLabel = fetchedAt ? `Updated ${formatRelative(fetchedAt)}` : 'Not loaded yet';

  return (
    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      {/* Mobile */}
      <div className="flex flex-col gap-2 sm:hidden">
        <Dropdown value={selectedAccountKey} onChange={onAccountChange} options={accountOptions} disabled={disabled} />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search by symbol..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className={`pl-10 ${focusRing}`}
          />
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <ToggleSwitch
              checked={mismatchesOnly}
              onChange={() => onMismatchesOnlyChange(!mismatchesOnly)}
              size="sm"
            />
            <span>Only mismatches</span>
          </label>
          <Button onClick={onRefresh} size="sm" leftIcon={<RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />} disabled={disabled || refreshing}>
            Refresh
          </Button>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{lastFetchedLabel}</div>
      </div>

      {/* Desktop */}
      <div className="hidden sm:flex flex-wrap items-center gap-2">
        <div className="min-w-[260px]">
          <Dropdown value={selectedAccountKey} onChange={onAccountChange} options={accountOptions} disabled={disabled} />
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search by symbol..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className={`pl-10 w-56 ${focusRing}`}
          />
        </div>

        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 ml-2 cursor-pointer">
          <ToggleSwitch
            checked={mismatchesOnly}
            onChange={() => onMismatchesOnlyChange(!mismatchesOnly)}
            size="sm"
          />
          <span>Only mismatches</span>
        </label>

        <div className="flex-1" />

        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{lastFetchedLabel}</span>

        <Button
          onClick={onRefresh}
          size="sm"
          leftIcon={<RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />}
          disabled={disabled || refreshing}
        >
          Refresh
        </Button>
      </div>
    </div>
  );
};

export default ReconciliationFilterBar;
